import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import API_ROUTES from "../hooks/apiRoutes";

/**
 * VINCULAR TV — rota /tv, aberta no CELULAR (não na TV).
 *
 * Dois caminhos chegam aqui:
 *  1. QR: a pessoa aponta a câmera pro código da TV e cai aqui com `?code=XXXXXX` já na URL.
 *     Se já estiver logada, a gente vincula SOZINHO (só apontou a câmera e pronto).
 *  2. Manual: digita o código que apareceu na TV.
 * Em ambos, isso liga o código à conta; a TV (que faz poll) recebe o token e entra.
 * Ver DeviceAuthController no backend.
 */
export default function VincularTv() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Código pode vir pré-preenchido pelo QR (…/tv?code=XXXXXX).
  const codeDaUrl = (params.get("code") || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const [code, setCode] = useState(codeDaUrl);
  const [estado, setEstado] = useState({ enviando: false, ok: false, erro: null });
  const logado = !!localStorage.getItem("token");

  const enviar = useCallback(async (valor) => {
    const limpo = String(valor || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (limpo.length < 6) {
      setEstado({ enviando: false, ok: false, erro: "O código tem 6 caracteres." });
      return;
    }
    setEstado({ enviando: true, ok: false, erro: null });
    try {
      const r = await fetch(API_ROUTES.DEVICE.link(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ code: limpo }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.message || "Código inválido ou expirado.");
      setEstado({ enviando: false, ok: true, erro: null });
    } catch (err) {
      setEstado({ enviando: false, ok: false, erro: err.message });
    }
  }, []);

  // Veio do QR (com ?code=) e já está logado? Libera sozinho — a pessoa só apontou a câmera.
  useEffect(() => {
    if (logado && codeDaUrl.length >= 6) enviar(codeDaUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!logado) {
    return (
      <div style={wrap}>
        <h1 style={h1}>Entrar na TV</h1>
        <p style={p}>
          Primeiro entre na sua conta aqui no celular. Depois volte a esta página para
          {codeDaUrl ? " confirmar o código." : " digitar o código que apareceu na TV."}
        </p>
        {codeDaUrl && <div style={{ ...input, marginBottom: 16 }}>{codeDaUrl}</div>}
        <button style={btn} onClick={() => navigate("/login")}>
          Fazer login
        </button>
      </div>
    );
  }

  if (estado.ok) {
    return (
      <div style={wrap}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>✅</div>
        <h1 style={h1}>TV liberada!</h1>
        <p style={p}>Pode voltar para a televisão — ela vai entrar sozinha em instantes.</p>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <h1 style={h1}>Entrar na TV</h1>
      <p style={p}>
        {codeDaUrl
          ? "Confira o código que apareceu na sua televisão e confirme."
          : "Digite o código que apareceu na sua televisão."}
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          enviar(code);
        }}
        style={{ display: "flex", flexDirection: "column", gap: 14, width: "100%", maxWidth: 320 }}
      >
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="EX: LCQX9D"
          maxLength={8}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          style={input}
        />
        {estado.erro && <div style={{ color: "#ff5a5f", fontSize: 14 }}>{estado.erro}</div>}
        <button type="submit" style={btn} disabled={estado.enviando}>
          {estado.enviando ? "Verificando…" : "Liberar a TV"}
        </button>
      </form>
    </div>
  );
}

const wrap = {
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  color: "#fff",
  background: "#141414",
  padding: "24px",
};
const h1 = { fontSize: 26, margin: "0 0 8px", fontWeight: 800 };
const p = { fontSize: 15, color: "#aaa", margin: "0 0 22px", lineHeight: 1.5, maxWidth: 340 };
const input = {
  fontSize: 28,
  letterSpacing: 6,
  textAlign: "center",
  textTransform: "uppercase",
  padding: "14px",
  borderRadius: 12,
  border: "2px solid #333",
  background: "#1e1e24",
  color: "#fff",
  fontWeight: 700,
};
const btn = {
  background: "#e50914",
  color: "#fff",
  border: "none",
  borderRadius: 12,
  padding: "14px 20px",
  fontSize: 16,
  fontWeight: 700,
  cursor: "pointer",
};
